import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { fetchBackendHealth, isBackendUnavailableError } from '../utils/backendHealth';

const RECOVERY_POLL_MS = 5_000;

/**
 * Reports whether the backend is confirmed down (database unreachable or the
 * service itself gone), so the app can show a maintenance overlay.
 *
 * Reactive by design: no polling while things work. A query or mutation error
 * that looks like an outage ({@link isBackendUnavailableError}) triggers one
 * /api/health probe; only a confirming probe flips to down. While down, the
 * probe repeats until the backend recovers, at which point every query is
 * invalidated so the UI refetches what it missed. This covers planned Cloud
 * SQL maintenance and unplanned outages with the same signal.
 */
export function useBackendMaintenance(): boolean {
  const queryClient = useQueryClient();
  const [isDown, setIsDown] = useState(false);
  // Subscription callbacks outlive renders; refs keep them reading current
  // state without resubscribing on every flip.
  const isDownRef = useRef(false);
  const isProbingRef = useRef(false);

  useEffect(() => {
    let disposed = false;
    let recoveryTimer: number | undefined;

    const probe = async () => {
      if (isProbingRef.current || disposed) {
        return;
      }
      isProbingRef.current = true;
      const health = await fetchBackendHealth();
      isProbingRef.current = false;
      if (disposed) {
        return;
      }
      if (health === 'down') {
        if (!isDownRef.current) {
          isDownRef.current = true;
          setIsDown(true);
        }
        recoveryTimer = window.setTimeout(() => void probe(), RECOVERY_POLL_MS);
      } else if (isDownRef.current) {
        isDownRef.current = false;
        setIsDown(false);
        void queryClient.invalidateQueries();
      }
    };

    const onError = (error: unknown) => {
      if (!isDownRef.current && isBackendUnavailableError(error)) {
        void probe();
      }
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        onError(event.action.error);
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        onError(event.action.error);
      }
    });

    return () => {
      disposed = true;
      if (recoveryTimer !== undefined) {
        window.clearTimeout(recoveryTimer);
      }
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient]);

  return isDown;
}
